/**
 * Lista de Boletas de Honorarios (BHE) del tenant.
 */
import { formatNumberForTenant } from '@/lib/locale'
import { useState } from 'react'
import { Upload, RefreshCw } from 'lucide-react'

import { Button } from '@/components/Button'
import { DataTable, type DataTableColumn, type SortState } from '@/components/ds/DataTable'
import { Pagination } from '@/components/ds/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { Badge } from '@/components/Badge'
import { useTenantLocale } from '@/lib/locale'
import { PAYMENT_STATUS_LABEL, type HonorariosBillListItem } from '../types'
import { usePermission } from '@/hooks/usePermission'
import { useHonorariosList } from '../hooks/useHonorarios'
import { HonorariosUploadModal } from './HonorariosUploadModal'
import { HonorariosDetailModal } from './HonorariosDetailModal'


function fmt(value: string | number | null): string {
  if (value === null || value === undefined) return '—'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return String(value)
  return formatNumberForTenant(num, 0)
}

const STATUS_LABEL: Record<string, { label: string; variant: 'blue' | 'emerald' | 'slate' | 'red' }> = {
  received: { label: 'Recibida', variant: 'blue' },
  parsed: { label: 'Parseada', variant: 'blue' },
  posted: { label: 'Contabilizada', variant: 'emerald' },
  discarded: { label: 'Descartada', variant: 'slate' },
  error: { label: 'Error', variant: 'red' },
}

export function HonorariosTab() {
  const { can } = usePermission()
  const canCreate = can('honorarios', 'create')
  const [uploadOpen, setUploadOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  // 🔴 El orden es CONTROLADO y viaja al backend: el endpoint pagina de a 25,
  // así que ordenar en el cliente ordenaría solo la página visible y quien
  // mira creería que ordenó la lista entera.
  const [sort, setSort] = useState<SortState | undefined>()
  const locale = useTenantLocale()

  const ordering = sort ? `${sort.dir === 'desc' ? '-' : ''}${sort.key}` : undefined
  const { data, isLoading, refetch, isRefetching } = useHonorariosList({ page, ordering })

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" leftIcon={<RefreshCw size={14} />} onClick={() => refetch()} isPending={isRefetching} pendingLabel="Actualizando…">
            Refrescar
          </Button>
        </div>
        {canCreate && (
          <Button leftIcon={<Upload size={14} />} onClick={() => setUploadOpen(true)}>
            Subir BHE
          </Button>
        )}
      </div>

      <DataTable
        rows={data?.results ?? []}
        rowKey={row => row.id}
        loading={isLoading}
        loadingNode={<TableSkeleton rows={6} cols={8} />}
        minWidth={900}
        caption="Boletas de honorarios recibidas"
        onRowClick={row => setDetailId(row.id)}
        sort={sort}
        onSortChange={s => { setSort(s); setPage(1) }}
        empty={
          <EmptyState
            title="Sin boletas de honorarios"
            hint="Subir una BHE manualmente o esperar a que llegue por correo desde el SII."
          />
        }
        columns={[
          {
            // ⚠️ Solo son ordenables las columnas que el backend declara en
            // `ordering_fields`. DRF ignora en silencio cualquier otra, o sea
            // la flecha se movería y la tabla no cambiaría.
            key: 'issue_date', header: 'Fecha', sortable: true, width: 110,
            render: row => row.issue_date ?? '—',
          },
          {
            key: 'bhe_number', header: 'N° BHE', width: 110,
            render: row => <span className="font-mono">{row.bhe_number || '—'}</span>,
          },
          {
            key: 'issuer', header: 'Emisor',
            render: row => (
              <>
                <div className="text-content">{row.issuer_name || '—'}</div>
                <div className="text-xs text-content-subtle font-mono">{row.issuer_rut}</div>
              </>
            ),
          },
          {
            key: 'total_honorarios', header: 'Total', numeric: true, sortable: true,
            render: row => fmt(row.total_honorarios),
          },
          {
            key: 'impuesto_honorarios', header: 'Retención', numeric: true,
            render: row => fmt(row.impuesto_honorarios),
          },
          {
            key: 'liquido_honorarios', header: 'Líquido', numeric: true,
            render: row => <span className="font-semibold">{fmt(row.liquido_honorarios)}</span>,
          },
          {
            key: 'status', header: 'Estado', width: 130,
            render: row => {
              const s = STATUS_LABEL[row.status] ?? { label: row.status, variant: 'slate' as const }
              return <Badge label={s.label} variant={s.variant} />
            },
          },
          {
            key: 'payment_status', header: 'Pago', width: 110,
            render: row => {
              if (row.status !== 'posted') return <span className="text-content-subtle">—</span>
              const p = PAYMENT_STATUS_LABEL[row.payment_status] ?? PAYMENT_STATUS_LABEL.pending
              return (
                <span title={row.payment_status === 'partial' ? `Saldo por pagar: ${fmt(row.pending_amount)}` : undefined}>
                  <Badge label={p.label} variant={p.variant} />
                </span>
              )
            },
          },
        ] satisfies DataTableColumn<HonorariosBillListItem>[]}
      />

      {/* 🔴 El paginador FALTABA: el endpoint devuelve de a 25 y la pantalla
          mostraba esa primera pagina como si fuera la lista completa. */}
      {(data?.count ?? 0) > 25 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data?.count ?? 0}
          onPageChange={setPage}
          locale={locale}
          className="mt-4"
        />
      )}

      {uploadOpen && (
        <HonorariosUploadModal
          onClose={() => setUploadOpen(false)}
          onUploaded={(id) => { setUploadOpen(false); setDetailId(id) }}
        />
      )}
      {detailId && (
        <HonorariosDetailModal id={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  )
}
